/**
 * Gesture-gated native-app handoff for use.x9.app.
 *
 * Opens custom-scheme URLs via a same-gesture <a> click —
 * never top-level location.href (blanks the tab) and never Android Intent
 * URLs (crashed cold starts).
 *
 * "Launched" is conservative: the tab must go hidden, then stay hidden
 * for BOUNCE_MS. Android Chrome often flashes hidden for a missing-app
 * scheme interstitial — bounce-back → treat as not installed.
 */

const HIDDEN_WAIT_MS = 900;
const BOUNCE_MS = 2200;

/**
 * Guards against a double tap firing the scheme twice. Keyed by URL: a request
 * for a *different* destination is a real second request, and collapsing it
 * into the first silently dropped it and reported the wrong attempt's outcome.
 */
let inFlight: { url: string; promise: Promise<HandoffResult> } | null = null;

export interface HandoffResult {
  launched: boolean;
}

export function buildCaptureHandoffUrl(token: string): string {
  return `x9://capture/${encodeURIComponent(token)}`;
}

/** Routine run open — native-only (PRD FR11 / use.x9.app/r/<runId>). */
export function buildRunHandoffUrl(runId: string): string {
  return `x9://run/${encodeURIComponent(runId)}`;
}

/** Bare scheme — opens the installed app (home / default handler). */
export const NATIVE_APP_HOME_SCHEME = 'x9://';

function openCustomScheme(schemeUrl: string): void {
  const anchor = document.createElement('a');
  anchor.href = schemeUrl;
  anchor.style.display = 'none';
  anchor.setAttribute('rel', 'noreferrer');
  document.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    window.setTimeout(() => {
      anchor.remove();
    }, HIDDEN_WAIT_MS + BOUNCE_MS + 300);
  }
}

/**
 * Attempt to open a custom-scheme URL in the native X9 app.
 * Returns whether the app likely took over (tab stayed hidden).
 */
export function attemptSchemeHandoff(schemeUrl: string): Promise<HandoffResult> {
  if (!schemeUrl || typeof schemeUrl !== 'string') {
    return Promise.resolve({ launched: false });
  }

  if (inFlight?.url === schemeUrl) return inFlight.promise;

  // `settle` can run before the promise is assigned (the scheme click throws
  // synchronously), so track completion here rather than comparing promises.
  const attempt = { settled: false };

  const promise = new Promise<HandoffResult>((resolve) => {
    let resolved = false;
    let hiddenAt: number | null = null;
    let bounceTimer: number | null = null;
    let waitTimer: number | null = null;

    const settle = (launched: boolean) => {
      if (resolved) return;
      resolved = true;
      document.removeEventListener('visibilitychange', onVisChange);
      if (waitTimer != null) window.clearTimeout(waitTimer);
      if (bounceTimer != null) window.clearTimeout(bounceTimer);
      attempt.settled = true;
      if (inFlight?.url === schemeUrl) inFlight = null;
      resolve({ launched });
    };

    function onVisChange() {
      if (document.visibilityState === 'hidden') {
        if (hiddenAt != null) return;
        hiddenAt = Date.now();
        if (waitTimer != null) {
          window.clearTimeout(waitTimer);
          waitTimer = null;
        }
        // Stay hidden this long → treat as real handoff.
        bounceTimer = window.setTimeout(() => {
          settle(document.visibilityState === 'hidden');
        }, BOUNCE_MS);
        return;
      }

      // Visible again — scheme interstitial / no app installed.
      if (hiddenAt != null) {
        settle(false);
      }
    }

    document.addEventListener('visibilitychange', onVisChange);

    // Never went hidden → not launched.
    waitTimer = window.setTimeout(() => {
      if (hiddenAt == null) {
        settle(false);
      }
    }, HIDDEN_WAIT_MS);

    try {
      openCustomScheme(schemeUrl);
    } catch {
      settle(false);
    }
  });

  if (!attempt.settled) inFlight = { url: schemeUrl, promise };
  return promise;
}

/**
 * Attempt to open the native X9 app with the capture token.
 */
export function attemptNativeAppHandoff(token: string): Promise<HandoffResult> {
  return attemptSchemeHandoff(buildCaptureHandoffUrl(token));
}

export function resetNativeAppHandoff(): void {
  inFlight = null;
}
